import type {
  EmailCompositionValue,
  EmailFieldErrors,
} from '@kula-ai/components';
import { isHtmlEffectivelyEmpty } from '@kula-ai/components';
import {
  isSharedEmailFromValue,
  parseSharedEmailId,
} from '../../hooks/useScheduleSenderOptions';

const SUBJECT_MAX_LENGTH = 150;

/**
 * Required merge tag on the slot-selection email body — without it,
 * the candidate has no way to reach the self-schedule picker.
 * Mirrors InterviewActivity's self-schedule panel check so edit-mode
 * and create-mode errors read the same.
 */
export const REQUIRED_SELF_SCHEDULE_VARIABLE = 'interview.self_schedule_link';

/** `true` when the content contains `{{interview.self_schedule_link}}`. */
function hasRequiredSelfScheduleLink(content: string): boolean {
  const pattern = new RegExp(
    `{{\\s*${REQUIRED_SELF_SCHEDULE_VARIABLE.replace('.', '\\.')}\\s*}}`
  );
  return pattern.test(content);
}

/**
 * Accept either:
 *   - A numeric user id (regular delegatable sender), or
 *   - A `shared:${id}` value emitted by `useScheduleSenderOptions` for
 *     shared mailboxes (`feSharedEmails` accounts).
 *
 * Anything else (empty string, `organizer` sentinel, malformed
 * shared prefix) is unset, so the required-field error fires.
 */
function isValidFromUserId(raw: string): boolean {
  if (!raw) return false;
  const trimmed = raw.trim();
  if (!trimmed) return false;
  if (isSharedEmailFromValue(trimmed)) {
    return parseSharedEmailId(trimmed) !== undefined;
  }
  const n = Number(trimmed);
  return Number.isFinite(n) && n > 0;
}

export interface ValidateSlotSelectionEmailOptions {
  /**
   * Skip the `to` required-field check. Set when the To field is
   * hidden (bulk-self-schedule with > 1 candidate — recipients derive
   * from `ats_application_ids[]` server-side, so there's nothing for
   * the user to fill in). Without this, validation fails on a row
   * the user can't see.
   */
  skipToCheck?: boolean;
}

export function validateSlotSelectionEmail(
  value: EmailCompositionValue,
  from: string,
  options?: ValidateSlotSelectionEmailOptions
): EmailFieldErrors | undefined {
  const errors: EmailFieldErrors = {};

  // Accept only a concrete delegatable-user id. The legacy organizer
  // sentinel is filtered out of `fromOptions` for scheduling, so
  // letting it pass here leaves the select visibly empty but reports
  // the form valid — the exact "form is empty in UI but passing
  // validation" report.
  if (!isValidFromUserId(from)) {
    errors.from = 'From is required';
  }

  // Self-schedule only sends to the candidate, but the field is still
  // required — empty means the backend has no recipient to email.
  // Bulk multi-candidate skips this — recipients are server-derived.
  if (!options?.skipToCheck) {
    const toRaw = value.to;
    const toEmpty = Array.isArray(toRaw)
      ? toRaw.filter(Boolean).length === 0
      : !toRaw || !String(toRaw).trim();
    if (toEmpty) {
      errors.to = 'To is required';
    }
  }

  const subject = value.subject?.trim() ?? '';
  if (!subject) {
    errors.subject = 'Subject is required';
  } else if (subject.length > SUBJECT_MAX_LENGTH) {
    errors.subject = `Subject must be at most ${SUBJECT_MAX_LENGTH} characters`;
  }

  const content = value.content ?? '';
  if (isHtmlEffectivelyEmpty(content)) {
    errors.content = 'Body is required';
  } else if (!hasRequiredSelfScheduleLink(content)) {
    // Same shape as the InterviewActivity validator — the step
    // renderer checks `errors.content.includes(...)` to decide
    // whether to show the red "Required variable" hint.
    errors.content = `Body must include {{${REQUIRED_SELF_SCHEDULE_VARIABLE}}}`;
  }

  return Object.keys(errors).length ? errors : undefined;
}
